import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from "react-query";
import config from "../config";
import axios, { AxiosError } from "axios";
import { Auth } from "../utils/auth";
import { UserInfo } from "@microsoft/teamsfx";
import { AnalysisResultDto, CheckPermissionsResult } from "./models/TenantOptimizer";

export const useGetUserInfo = (): UseQueryResult<UserInfo, unknown> => {
    const query = useQuery("userInfo", async () => {
        return await Auth.getUserInfo();
    }, { staleTime: 100_000 });
    return query;
}

export const useHasLicenseOptimizerPermissions = (): UseQueryResult<CheckPermissionsResult, unknown> => {
	const credential = Auth.getCredential();
	const query = useQuery({
		queryKey: ["hasLicenseOptimizerPermissions"],
		queryFn: async () => {
			const userInfo = await credential.getUserInfo();
			const apiUrl = `${config.tenantOptimizerBackendUrl}/checkPermissions?tenantId=${userInfo.tenantId}`;
			const result = await axios.get<CheckPermissionsResult>(apiUrl);
			return result.data;
		},
		onError: async (error) => {
			console.error(error);
		},
		retry: false,
		staleTime: 60_000
	});
	return query;
};

export const useGetLastAnalysisResult = (enabled: boolean) : UseQueryResult<AnalysisResultDto | null> => {
	return useQuery<AnalysisResultDto>('lastAnalysisResult', async () => {
		const token = await Auth.getToken([]);
		const apiUrl = `${config.tenantOptimizerBackendUrl}/lastAnalysisResult`;
		const response = await axios.get(apiUrl, {
			headers: {
				Authorization: `Bearer ${token.token}`
			}
		});
		return response.data;
	}, { staleTime: 1000, enabled: !!enabled });
};

export const useRunAnalyticsByUserMutation = (onSuccess: (data: AnalysisResultDto) => void, onError: (error: AxiosError) => void) : UseMutationResult<AnalysisResultDto, AxiosError, string> => {
	const queryClient = useQueryClient();
	const mutation = useMutation<AnalysisResultDto, AxiosError, string>({
		mutationFn: async () => {
			const token = await Auth.getToken([]);
			const response = await axios<AnalysisResultDto>({
				method: "POST",
				url: `${config.tenantOptimizerBackendUrl}/runByUser`, 
				headers: {
				  Authorization: `Bearer ${token.token}`
				}
			});
			return response.data;
		},
		onSuccess: (data) => {
			onSuccess(data);
			queryClient.setQueryData(["lastAnalysisResult"], data)
		},
		onError: onError
	});
	return mutation;
};
